/**
 * Vector Search — Semantic search against Qdrant via BNova AI-services.
 * Allows admins to search the vector database from the business dashboard.
 */

import { useState } from "react";

// @mui material components
import Grid from "@mui/material/Grid";
import Card from "@mui/material/Card";
import Icon from "@mui/material/Icon";
import Chip from "@mui/material/Chip";
import CircularProgress from "@mui/material/CircularProgress";
import Divider from "@mui/material/Divider";

// Material Dashboard 2 React components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDInput from "components/MDInput";
import MDButton from "components/MDButton";

// Material Dashboard 2 React example components
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import Footer from "examples/Footer";

// Services
import { searchVector, getVectorHealth } from "services/dashboardService";

const glassCardSx = {
  background: "linear-gradient(135deg, rgba(255,255,255,0.06) 0%, rgba(255,255,255,0.02) 100%)",
  backdropFilter: "blur(12px)",
  border: "1px solid rgba(255,255,255,0.08)",
  borderRadius: "16px",
  transition: "all 0.3s cubic-bezier(0.4,0,0.2,1)",
  "&:hover": {
    transform: "translateY(-2px)",
    boxShadow: "0 12px 40px rgba(0,0,0,0.3)",
  },
};

function VectorSearch() {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [health, setHealth] = useState(null);
  const [healthLoading, setHealthLoading] = useState(false);
  const [searched, setSearched] = useState(false);

  const handleSearch = async () => {
    if (!query.trim()) return;
    setLoading(true);
    setError(null);
    setSearched(true);

    try {
      const data = await searchVector(query, 10);
      setResults(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error("Vector search failed:", err);
      setError(err.response?.data?.error || "Search failed. Is the AI-services backend running?");
      setResults([]);
    } finally {
      setLoading(false);
    }
  };

  const handleHealthCheck = async () => {
    setHealthLoading(true);
    try {
      const data = await getVectorHealth();
      setHealth(data);
    } catch (err) {
      setHealth({ status: "unhealthy", error: err.message });
    } finally {
      setHealthLoading(false);
    }
  };

  const handleKeyDown = (e) => {
    if (e.key === "Enter") handleSearch();
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <MDBox py={3}>
        {/* Search bar */}
        <Card sx={{ ...glassCardSx, p: 3, mb: 3 }}>
          <MDBox display="flex" alignItems="center" gap={2} mb={2}>
            <Icon sx={{ color: "#26c6da", fontSize: 28 }}>manage_search</Icon>
            <MDTypography variant="h5" color="white">
              Vector Search (Qdrant)
            </MDTypography>
          </MDBox>
          <MDTypography variant="body2" color="text" mb={3}>
            Search your document library using semantic similarity powered by Qdrant vector
            database.
          </MDTypography>
          <Grid container spacing={2} alignItems="center">
            <Grid item xs={12} md={8}>
              <MDInput
                fullWidth
                label="Enter your search query..."
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyDown={handleKeyDown}
                sx={{
                  "& .MuiInputBase-root": {
                    bgcolor: "rgba(255,255,255,0.05)",
                    borderRadius: "12px",
                  },
                }}
              />
            </Grid>
            <Grid item xs={6} md={2}>
              <MDButton
                fullWidth
                variant="gradient"
                color="info"
                onClick={handleSearch}
                disabled={loading || !query.trim()}
              >
                {loading ? <CircularProgress size={20} color="inherit" /> : "Search"}
              </MDButton>
            </Grid>
            <Grid item xs={6} md={2}>
              <MDButton
                fullWidth
                variant="outlined"
                color="success"
                onClick={handleHealthCheck}
                disabled={healthLoading}
              >
                {healthLoading ? <CircularProgress size={20} color="inherit" /> : "Health Check"}
              </MDButton>
            </Grid>
          </Grid>

          {/* Health status */}
          {health && (
            <MDBox mt={2} display="flex" alignItems="center" gap={1}>
              <Chip
                label={health.status === "healthy" ? "Healthy" : "Unhealthy"}
                size="small"
                sx={{
                  bgcolor:
                    health.status === "healthy" ? "rgba(102,187,106,0.2)" : "rgba(244,67,54,0.2)",
                  color: health.status === "healthy" ? "#66bb6a" : "#f44336",
                  fontWeight: 600,
                }}
              />
              <MDTypography variant="caption" color="text">
                Vector DB: {health.service || "qdrant"}
              </MDTypography>
            </MDBox>
          )}
        </Card>

        {/* Error */}
        {error && (
          <Card sx={{ ...glassCardSx, p: 3, mb: 3, border: "1px solid rgba(244,67,54,0.3)" }}>
            <MDTypography variant="body2" color="error">
              {error}
            </MDTypography>
          </Card>
        )}

        {/* Results */}
        {searched && !loading && results.length === 0 && !error && (
          <Card sx={{ ...glassCardSx, p: 4, textAlign: "center" }}>
            <Icon sx={{ fontSize: 48, color: "rgba(255,255,255,0.2)", mb: 1 }}>search_off</Icon>
            <MDTypography variant="h6" color="text">
              No results found
            </MDTypography>
            <MDTypography variant="body2" color="text" sx={{ opacity: 0.6 }}>
              Try a different query or check if documents are indexed.
            </MDTypography>
          </Card>
        )}

        {results.length > 0 && (
          <Grid container spacing={2}>
            {results.map((result, index) => (
              <Grid item xs={12} key={index}>
                <Card
                  sx={{
                    ...glassCardSx,
                    p: 3,
                    animation: `fadeInUp 0.3s ease-out ${index * 0.05}s both`,
                    "@keyframes fadeInUp": {
                      from: { opacity: 0, transform: "translateY(10px)" },
                      to: { opacity: 1, transform: "translateY(0)" },
                    },
                  }}
                >
                  <MDBox display="flex" justifyContent="space-between" alignItems="flex-start">
                    <MDBox flex={1}>
                      <MDBox display="flex" alignItems="center" gap={1} mb={1}>
                        <Chip
                          label={`#${index + 1}`}
                          size="small"
                          sx={{
                            bgcolor: "rgba(38,198,218,0.15)",
                            color: "#26c6da",
                            fontWeight: 700,
                            minWidth: 40,
                          }}
                        />
                        {result.score !== undefined && (
                          <Chip
                            label={`Score: ${(result.score * 100).toFixed(1)}%`}
                            size="small"
                            sx={{
                              bgcolor:
                                result.score > 0.8
                                  ? "rgba(102,187,106,0.15)"
                                  : "rgba(255,167,38,0.15)",
                              color: result.score > 0.8 ? "#66bb6a" : "#ffa726",
                              fontWeight: 600,
                            }}
                          />
                        )}
                        {result.metadata?.source_type && (
                          <Chip
                            label={result.metadata.source_type}
                            size="small"
                            variant="outlined"
                            sx={{
                              borderColor: "rgba(255,255,255,0.2)",
                              color: "rgba(255,255,255,0.6)",
                            }}
                          />
                        )}
                      </MDBox>
                      <MDTypography variant="body2" color="white" sx={{ lineHeight: 1.6 }}>
                        {result.content || result.text || JSON.stringify(result).slice(0, 300)}
                      </MDTypography>
                    </MDBox>
                  </MDBox>
                </Card>
              </Grid>
            ))}
          </Grid>
        )}
      </MDBox>
      <Footer />
    </DashboardLayout>
  );
}

export default VectorSearch;
